import React from "react";
import { useLanguage } from "../i18n/LanguageContext";
import TrustStrip from "../components/TrustStrip";
import BookingCTA from "../components/BookingCTA";
import { IMG } from "../lib/constants";
import { Check, X } from "lucide-react";

const Aftercare = () => {
    const { t } = useLanguage();
    const a = t.aftercare;

    return (
        <>
            {/* Hero Image with overlaid SEO text */}
            <section
                data-testid="aftercare-hero"
                className="relative min-h-[60vh] md:min-h-[65vh] overflow-hidden"
            >
                <img
                    src={IMG.aftercareHero}
                    alt="Permanent makeup aftercare and healing guide Beauty Ink LV"
                    className="w-full h-full absolute inset-0 object-cover"
                    style={{ objectPosition: "0% 15%" }}
                />
                {/* Gradient overlay for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent from-40% via-black/30 to-black/75" />
                {/* Text overlay — centred in lower half */}
                <div className="absolute bottom-[18%] md:bottom-[22%] left-0 right-0 px-8 md:px-16">
                    <div className="font-label text-[11px] tracking-widest text-white/80 mb-3 uppercase">
                        {t.nav.aftercare}
                    </div>
                    <h1 className="font-serif text-4xl md:text-6xl text-white leading-[1.0] mb-4 max-w-3xl drop-shadow-lg">
                        {a.heroTitle}
                    </h1>
                    <p className="text-white/85 text-base md:text-lg max-w-2xl leading-relaxed drop-shadow">
                        {a.heroSub}
                    </p>
                </div>
            </section>

            <TrustStrip variant="light" />

            {/* Sections */}
            <section className="bg-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[1200px] mx-auto px-6 space-y-20">
                    {a.sections.map((section, idx) => (
                        <div
                            key={idx}
                            data-testid={`aftercare-section-${idx}`}
                            className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 pb-20 border-b border-[#3D3D24]/15 last:border-0 last:pb-0"
                        >
                            <div>
                                <div className="font-label text-[11px] text-[#D4A95A] mb-4">
                                    0{idx + 1}
                                </div>
                                <h2 className="font-serif text-3xl md:text-4xl text-[#3D3D24] leading-tight">
                                    {section.title}
                                </h2>
                            </div>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <div className="font-label text-[10px] text-[#3D3D24] mb-4 flex items-center gap-2">
                                        <span className="w-5 h-5 bg-[#D4A95A] flex items-center justify-center">
                                            <Check size={12} className="text-[#3D3D24]" strokeWidth={3} />
                                        </span>
                                        Do
                                    </div>
                                    <ul className="space-y-3">
                                        {section.dos.map((d, i) => (
                                            <li
                                                key={i}
                                                data-testid={`aftercare-do-${idx}-${i}`}
                                                className="text-sm text-[#3D3D24]/85 leading-relaxed pl-5 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-[#D4A95A]"
                                            >
                                                {d}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <div className="font-label text-[10px] text-[#3D3D24] mb-4 flex items-center gap-2">
                                        <span className="w-5 h-5 bg-[#3D3D24] flex items-center justify-center">
                                            <X size={12} className="text-[#FFFFF3]" strokeWidth={3} />
                                        </span>
                                        Don&apos;t
                                    </div>
                                    <ul className="space-y-3">
                                        {section.donts.map((d, i) => (
                                            <li
                                                key={i}
                                                data-testid={`aftercare-dont-${idx}-${i}`}
                                                className="text-sm text-[#3D3D24]/85 leading-relaxed pl-5 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-[#3D3D24]"
                                            >
                                                {d}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="bg-[#F5F2E4] p-10 md:p-14 mt-10">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">
                            Important
                        </div>
                        <h3 className="font-serif text-3xl md:text-4xl text-[#3D3D24] mb-5">
                            {a.touchUp.title}
                        </h3>
                        <p className="text-[#3D3D24]/85 leading-relaxed max-w-2xl">
                            {a.touchUp.body}
                        </p>
                    </div>
                </div>
            </section>

            <BookingCTA variant="dark" testid="booking-cta-aftercare" />
        </>
    );
};

export default Aftercare;
